import mediator from './mediator';

export interface DownloadFileHandle {
  createWritable(): Promise<WritableStream<Uint8Array>>;
}

type PickerWindow = Window & {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    startIn: 'downloads';
  }) => Promise<DownloadFileHandle>;
};

export const supportsFileDownload = () =>
  typeof (window as PickerWindow).showSaveFilePicker === 'function';

/**
 * Attempt to download a file using the File System Access API. If the download fails, the returned Blob can be used as a fallback.
 * @param url URL to attempt to download
 * @param filename Name of the file to save the downloaded content as
 * @returns Blob response, to be used as a fallback
 */
export const downloadFileFromStream = async (
  url: string,
  filename: string,
  signal?: AbortSignal,
) => {
  if (signal?.aborted) {
    throw new DOMException('Download cancelled', 'AbortError');
  }

  const fileHandle = await (window as PickerWindow).showSaveFilePicker?.({
    suggestedName: filename,
    startIn: 'downloads',
  });

  if (signal?.aborted) {
    throw new DOMException('Download cancelled', 'AbortError');
  }

  const result = await mediator.request('api:invoke', {
    method: 'GET',
    url,
    responseType: 'blob',
    fileHandle,
    signal,
  });

  return result;
};

/**
 * Download a file from a Blob URL by creating a temporary anchor element and triggering a click event.
 * @param blobUrl URL of the Blob object to download
 * @param filename Name of the file to download the Blob as
 */
export const downloadFileFromBlob = (blobUrl: string, filename: string) => {
  const link = document.createElement('a');

  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);

  link.click();
  link.remove();
};
